import { Clipboard } from '@angular/cdk/clipboard';
import { Component, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { faCopy } from '@fortawesome/free-solid-svg-icons';
import { NGXLogger } from "ngx-logger";
import { Chapter } from "src/app/models/chapters.model";
import { Course } from "src/app/models/course.model";
import { Quiz } from "src/app/models/quiz.model";
import { School } from "src/app/models/school.model";
import { ChaptersService } from "src/app/services/chapters.service";
import { CoursesService } from "src/app/services/courses.service";
import { QuizesService } from "../../../services/quizes.service";

@Component({
  selector: "app-quiz-list",
  templateUrl: "./quiz-list.component.html",
  styleUrls: ["./quiz-list.component.css"],
})
export class QuizListComponent implements OnInit {
  faCopy = faCopy;

  first = 0;
  rows = 15;
  sortField;
  sortOrder;
  filters;
  totalRecords = 0;
  /** Last PrimeNG lazy-load event, so we can re-run the current page after edits. */
  lazyEvent: any = null;

  displayDialog: boolean;

  selectedQuiz: Quiz;

  newQuiz: boolean;

  quizzes: Quiz[] = [];
  quiz: Quiz;

  cols: any[];

  schoolsSelect: School[];

  errorMessage;
  successMessage;

  dateFilters: any;

  quizLocations = [];

  courses = [];

  quizForm = new FormGroup({
    QuizTitle: new FormControl("", Validators.required),
    QuizLocation: new FormControl("", Validators.required),
    QuizOrder: new FormControl("", Validators.required),
    QuestionLimit: new FormControl(""),
  });

  loading = true;
  link;

  /** Set when routed as courses/:courseId/quizzes, to scope the list server-side to one course. */
  courseId: number | null = null;
  /** Set when routed as courses/:courseId/chapters/:chapterId/quizzes, to further scope to one chapter. */
  chapterId: number | null = null;
  /** Course/chapter names for the scoped-view heading, resolved from the already-loaded dropdown data. */
  courseName: string | null = null;
  chapterName: string | null = null;

  constructor(
    private logger: NGXLogger,
    private quizesService: QuizesService,
    public router: Router,
    private activateRoute: ActivatedRoute,
    private chapterService: ChaptersService,
    private coursesService: CoursesService,
    private clipboard: Clipboard,
  ) { }

  // Load just the course list for the Course dropdown. Chapters are fetched on
  // demand per course (see loadQuizLocationsFor) instead of pre-fetching every
  // course's chapters up front, which caused a flood of /courses/{id}/chapters calls.
  async loadCourses() {
    const courses = await this.coursesService.findAll();
    this.courses = courses.map(i => ({ label: i.courseName, value: i.id }));
  }

  /** Fetch (cached) chapters for a single course and populate the Location dropdown. */
  async loadQuizLocationsFor(courseId: any) {
    if (!courseId) {
      this.quizLocations = [];
      return;
    }
    const chapters = await this.chapterService.getChaptersBy(courseId);
    this.quizLocations = (chapters || []).map(i => ({ label: i.ChapterName, value: i.ID.toString() }));
  }

  /**
   * PrimeNG lazy loader: fetches only the current page of quizzes (already
   * enriched with courseId/courseName) from the server, instead of pulling every
   * course's quizzes and paginating on the client.
   */
  async loadQuizzesLazy(event: any) {
    this.lazyEvent = event;
    this.loading = true;
    this.first = event && event.first != null ? event.first : 0;
    this.rows = event && event.rows != null ? event.rows : this.rows;

    const pageParams = {
      first: this.first,
      rows: this.rows,
      sortField: event && event.sortField,
      sortOrder: event && event.sortOrder,
      filters: event && event.filters,
    };
    const res = this.chapterId
      ? await this.quizesService.findAllPagedByCourseAndChapter(this.courseId, this.chapterId, pageParams)
      : await this.quizesService.findAllPagedByCourse(this.courseId, pageParams);

    this.quizzes = res.data || [];
    this.totalRecords = res.totalRecords || 0;
    this.loading = false;
  }

  /** Re-run the current page after create/update/delete. */
  async reload() {
    await this.loadQuizzesLazy(this.lazyEvent || { first: this.first, rows: this.rows });
  }

  async ngOnInit() {
    const courseIdParam = this.activateRoute.snapshot.paramMap.get("courseId");
    this.courseId = courseIdParam ? +courseIdParam : null;
    const chapterIdParam = this.activateRoute.snapshot.paramMap.get("chapterId");
    this.chapterId = chapterIdParam ? +chapterIdParam : null;

    // Load the course list for the dropdown; chapters load on demand per course.
    await this.loadCourses();

    this.cols = [
      { field: "QuizID", header: "ID" },
      { field: "QuizTitle", header: "Title" },
      { field: "QuizLocation", header: "Chapter" },
      { field: "QuizOrder", header: "Order" },
      { field: "QuestionLimit", header: "Question Limit" },
    ];

    if (this.courseId) {
      const course = this.courses.find(c => `${c.value}` === `${this.courseId}`);
      this.courseName = course ? course.label : null;
    }
    if (this.chapterId) {
      this.cols = this.cols.filter(c => c.field !== "QuizLocation");
      await this.loadQuizLocationsFor(this.courseId);
      const chapter = this.quizLocations.find(l => `${l.value}` === `${this.chapterId}`);
      this.chapterName = chapter ? chapter.label : null;
    }
  }

  async showDialogToAdd() {
    this.newQuiz = true;
    this.displayDialog = true;
    this.clearAll();

    if (this.courseId) {
      this.quiz['courseId'] = this.courseId;
      await this.loadQuizLocationsFor(this.courseId);

      if (this.chapterId) {
        const chapterId = this.chapterId;
        const location = this.quizLocations.find(l => `${l.value}` === `${chapterId}`);
        this.quiz.QuizLocation2 = `${chapterId}`;
        this.quiz.QuizLocation = location ? location.label : this.quiz.QuizLocation;
      }
    }
  }

  save() {
    let p: Promise<any>;
    if (this.newQuiz) {
      const { courseId, courseName, ...quiz } = <any>this.quiz;
      p = this.quizesService.create(courseId, quiz);
    } else {
      const { courseId, courseName, ...quiz } = <any>this.quiz;
      p = this.quizesService.update(courseId, quiz);
    }

    return p
      .then(() => {
        this.reload();
        this.successMessage = "Quiz saved successfully";
      })
      .catch((err) => {
        if (
          err &&
          err.status === 400 &&
          err.error &&
          err.error.error &&
          err.error.error.description
        ) {
          this.errorMessage = err.error.error.description;
        }
      });
  }

  async onRowSelect(event) {
    this.newQuiz = false;
    this.clearAll();
    this.quiz = event.data;
    await this.loadQuizLocationsFor(this.quiz['courseId']);
    this.displayDialog = true;
    this.link = `/courses/${this.quiz['courseId']}/chapters/${this.quiz.QuizLocation2}/quizzes/${this.quiz.QuizID}`;
    this.logger.info(`Link: ${this.link}`);
  }

  clearAll() {
    this.quiz = {} as Quiz;
    this.quizLocations = [];
    this.errorMessage = null;
    this.successMessage = null;
    this.link = null;
  }

  onChangeLocation(event) {
    console.log(`Changing location to ${event.value}`);
    let quizLocation = this.quizLocations.find((ql) => ql.value == event.value);
    this.quiz.QuizLocation = quizLocation.label;
    this.quiz.QuizLocation2 = quizLocation.value;
  }

  async onChangeCourse(event) {
    await this.loadQuizLocationsFor(event.value);
  }

  gotoQuestions(event) {
    this.router.navigate(["admin", "courses", this.quiz["courseId"], "quizzes", this.quiz.QuizID, "questions"]);
  }

  async delete() {
    try {
      this.errorMessage = null;
      await this.quizesService.delete(this.quiz['courseId'], this.quiz.QuizID);
      this.successMessage = "Quiz deleted successfully";
      this.quiz = {} as Quiz;
      await this.reload();
    } catch (err) {
      console.error(err);
      this.errorMessage = err;
    }
  }

  handleClose() {
    this.displayDialog = false;
    this.clearAll();
    this.logger.info(`Closing dialog...`);
  }
}
